import {
  FILE_SIZE_LIMITS,
  formatFileSize,
} from "@mg/schemas/src/commons/file-limits";
import { Plus } from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  Flex,
  Grid,
  Text,
  VisuallyHidden,
} from "@radix-ui/themes";
import { useEffect, useState } from "react";

import { FileDropzone } from "../../../../../../components/FileDropzone";
import { UploadProgress } from "../../../../../../components/UploadProgress";
import { useUploadProgress } from "../../../../../../hooks/useUploadProgress";
import { uploadFile } from "../../../../../../services/upload";

export type Asset = {
  source: string;
  type: "image" | "video" | "file";
};

type CreateVersionDialogProps = {
  isPending: boolean;
  open: boolean;
  onOpenChange(open: boolean): void;
  onSubmitAssets(assets: Asset[]): void;
};

const dataTestIdScope = "create-version-dialog" as const;

export default function CreateVersionDialog(props: CreateVersionDialogProps) {
  const { isPending, open, onOpenChange, onSubmitAssets } = props;

  const [assets, setAssets] = useState<Asset[]>([]);
  const {
    uploadState,
    startUpload,
    updateProgress,
    setError,
    completeUpload,
    resetUpload,
  } = useUploadProgress();

  async function handleUpload(files?: File[] | null) {
    if (files != null && files.length > 0) {
      const fileList = Array.from(files);

      // Start upload for the first file (we'll enhance this for multiple files later)
      const firstFile = fileList[0];
      startUpload(firstFile.name, firstFile.size);

      try {
        const assets = await Promise.all(
          fileList.map(async (file, _index) => {
            const { key } = await uploadFile(
              file,
              {
                onProgress: (progress) => {
                  // Update progress for the current file
                  updateProgress(progress);
                },
              },
              true,
            );

            return {
              source: "https://static.puntt.ai/" + key,
              // @ts-expect-error TS1355: we know the type
              type: (file.type.includes("image")
                ? "image"
                : file.type.includes("video")
                  ? "video"
                  : "file") as const,
            };
          }),
        );

        completeUpload();
        return assets;
      } catch (error) {
        setError(error instanceof Error ? error.message : "Upload failed");
        return [];
      }
    }

    return [];
  }

  useEffect(() => {
    if (!open) {
      setAssets([]);
      resetUpload();
      // Unfortunately, we can't also abort in-flight uploads without a decent
      // refactor to support AbortControllers. Soon(TM). The FileDropzone
      // unmounts with the dialog content, so its field clears itself.
    }
  }, [open, resetUpload]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={onOpenChange}
      data-testid={dataTestIdScope}
    >
      <Dialog.Content
        maxWidth="450px"
        data-testid={`${dataTestIdScope}_content`}
      >
        <Dialog.Title data-testid={`${dataTestIdScope})_title`}>
          Upload New Version
        </Dialog.Title>
        <VisuallyHidden>
          <Dialog.Description data-testid={`${dataTestIdScope}_description`}>
            Upload a new version of your design for review.
          </Dialog.Description>
        </VisuallyHidden>

        <Grid gap="4">
          <Grid gap="2">
            <Text weight="medium" size="1">
              Files for review
            </Text>

            <FileDropzone
              multiple
              maxSizeBytes={FILE_SIZE_LIMITS.UPLOAD.MAX_SIZE_BYTES}
              helperText={`Max file size: ${formatFileSize(
                FILE_SIZE_LIMITS.UPLOAD.MAX_SIZE_BYTES,
              )}`}
              ariaLabel="Upload files for review"
              onFiles={async (files) => {
                const uploaded = await handleUpload(files);
                setAssets(uploaded);
              }}
              onClear={() => setAssets([])}
              data-testid={`${dataTestIdScope}_dropzone`}
              inputTestId={`${dataTestIdScope}_file-input`}
            />
          </Grid>

          {/* Show upload progress inline */}
          {uploadState.isUploading && (
            <UploadProgress
              isUploading={uploadState.isUploading}
              progress={uploadState.progress}
              fileName={uploadState.fileName}
              error={uploadState.error}
              isComplete={uploadState.isComplete}
              variant="inline"
            />
          )}

          <Flex gap="2" align="center" justify="end">
            <Dialog.Close data-testid={`${dataTestIdScope}_close`}>
              <Button variant="soft">Cancel</Button>
            </Dialog.Close>

            <Button
              disabled={isPending || uploadState.isUploading || !assets.length}
              loading={isPending || uploadState.isUploading}
              onClick={() => onSubmitAssets(assets)}
              data-testid={`${dataTestIdScope}_submit-upload-cta`}
            >
              <Plus />
              Upload New Version
            </Button>
          </Flex>
        </Grid>
      </Dialog.Content>
    </Dialog.Root>
  );
}
